import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetPaymentTransactionMutation } from '@hooks-api/fortune/useApiGetPaymentTransactionMutation'
import { type PaymentTransactionId } from '@hooks-api/types/dataModel/eCommercePayments'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { useRef } from 'react'

interface UseFpB2bPaymentTransactionPollingProps {
  paymentTransactionId: PaymentTransactionId
  interval?: number
  basePath: string
}

export const useFpB2bPaymentTransactionPolling = ({
  paymentTransactionId,
  interval = 5000,
  basePath,
}: UseFpB2bPaymentTransactionPollingProps) => {
  const timerId = useRef<NodeJS.Timeout | null>(null)

  const { trigger: triggerMemberTransaction } = useApiGetPaymentTransactionMutation()

  const pollOrderStatus = useCallbackEvent(async (): Promise<FastpassOrderStatus | undefined> => {
    if (paymentTransactionId) {
      try {
        const memberResponse = await triggerMemberTransaction(
          {
            id: String(paymentTransactionId),
          },
        )
        return memberResponse.data.data.status
      }
      catch (error) {
        console.error(`[useFpB2bPaymentTransactionPolling] ${error}`)
        return undefined
      }
    }
    else {
      console.error(`[useFpB2bPaymentTransactionPolling] paymentTransactionId: ${paymentTransactionId}`)
      return undefined
    }
  })

  const stopPolling = useCallbackEvent(() => {
    if (timerId.current) {
      clearInterval(timerId.current)
      timerId.current = null
    }
  })

  const startPolling = useCallbackEvent(async () => {
    timerId.current = setInterval(async () => {
      const status = await pollOrderStatus()
      const completedStatuses = [
        FastpassOrderStatus.COMPLETED,
        FastpassOrderStatus.FAILED,
      ]
      const shouldStop = !!(status && completedStatuses.includes(status))
      if (shouldStop) {
        stopPolling()
      }
      if (status === FastpassOrderStatus.COMPLETED) handleOrderComplete()
      if (status === FastpassOrderStatus.FAILED) handleOrderFailed()
    }, interval)
  })

  const handleOrderComplete = useCallbackEvent(() => {
    window.location.href = `${window.location.origin}${basePath}/thankyou/${paymentTransactionId}`
  })

  const handleOrderFailed = useCallbackEvent(() => {
    window.location.href = `${window.location.origin}${basePath}/checkout?type=failedPayment`
  })

  return {
    startPolling,
    stopPolling,
  }
}
